import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Container, Eyebrow, Button, Icon } from "@/brand/ui";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const inputStyle = {
  width: "100%", background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: 2,
  padding: "12px 14px", fontSize: 14, color: "var(--navy)", fontFamily: "var(--font-body)", outline: "none",
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.error) toast.error(location.state.error);
  }, [location.state]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/painel";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); toast.success("Sessão iniciada."); navigate("/painel"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--navy)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <Container narrow style={{ maxWidth: 440 }}>
        <Link to="/" data-testid="back-to-portal" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.6)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", textDecoration: "none", marginBottom: 32 }}>
          <span style={{ transform: "rotate(180deg)" }}><Icon name="arrow" size={14} /></span> Voltar ao portal
        </Link>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <div style={{ width: 96, height: 96, padding: 12, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)" }}>
            <img src="/brand/logo-square.png" alt="D.A.I." style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
        </div>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <Eyebrow inverse gold withRule={false}>DAI OS · Acesso da Equipa</Eyebrow>
        </div>
        <form onSubmit={submit} data-testid="login-form" style={{ background: "var(--bg-elevated)", borderRadius: 4, padding: 36, display: "flex", flexDirection: "column", gap: 20 }}>
          <label style={{ display: "block" }}>
            <span style={{ display: "block", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500, marginBottom: 8 }}>Email</span>
            <input data-testid="login-email" type="email" style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="acesso.indisponivel@example.invalid" />
          </label>
          <label style={{ display: "block" }}>
            <span style={{ display: "block", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500, marginBottom: 8 }}>Senha</span>
            <input data-testid="login-password" type="password" style={inputStyle} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </label>
          <Button type="submit" disabled={loading} testId="login-submit" style={{ width: "100%" }}>{loading ? "Entrando…" : "Entrar"}</Button>
          <p style={{ fontSize: 12, color: "var(--silver-mid)", textAlign: "center", margin: 0 }}>Acesso interno indisponivel: acesso.indisponivel@example.invalid · REMOVIDO</p>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
            <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
            <span style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--silver-mid)" }}>ou</span>
            <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
          </div>
          <button type="button" onClick={googleLogin} data-testid="google-login-btn"
            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, background: "var(--paper)", color: "var(--navy)", border: "1px solid var(--line)", borderRadius: 2, padding: "14px 0", fontSize: 13, letterSpacing: "0.04em", fontWeight: 500, cursor: "pointer" }}>
            <GoogleIcon /> Entrar com Google
          </button>
          <p style={{ fontSize: 11, color: "var(--silver-mid)", textAlign: "center", margin: 0 }}>Acesso Google restrito à equipa autorizada</p>
        </form>
      </Container>
    </div>
  );
}
